import type { ReaderDocument } from "@/evaluation"
import type { ResultMatrix } from "@/matrix"
import type { DateTimeString, Uuid } from "@/utils"

/**
 * Mirrors backend/src/Assignment/AssignmentStatus.php
 */
export enum AssignmentStatus {
  Pending = "pending",
  InProgress = "in_progress",
  Submitted = "submitted",
  Evaluated = "evaluated",
  Revoked = "revoked",
}

/**
 * An assignment a respondent still has to act on: it is theirs to start or
 * continue. Mirrors backend AssignmentStatus::isActive(); used both for the
 * My Profiles "Active" section and the post-login default landing decision.
 */
export const isActiveStatus = (status: AssignmentStatus): boolean =>
  status === AssignmentStatus.Pending || status === AssignmentStatus.InProgress

/**
 * Mirrors backend/src/Http/Page.php
 */
export type Page<TItem> = {
  readonly items: TItem[]
  readonly total: number
}

/**
 * A user as embedded in an assignment payload (the api + api_requester
 * serialisation groups)
 */
export type UserRef = {
  readonly id: Uuid
  readonly createdAt: DateTimeString
  readonly initials: string
  readonly name: string
  readonly email: string | null
}

/**
 * A tenant as embedded in an assignment payload
 */
export type TenantRef = {
  readonly id: Uuid
  readonly createdAt: DateTimeString
  readonly name: string
  readonly slug: string
  readonly defaultLocale: string
}

/**
 * A brief as embedded on the workspace surface (the description is
 * admin-only, so it is absent here)
 */
export type BriefRef = {
  readonly id: Uuid
  readonly createdAt: DateTimeString
  readonly name: string
  readonly archived: boolean
}

/**
 * A published evaluation serialised inline with an assignment
 */
export type EvaluationView = {
  readonly publishedAt: DateTimeString | null
  readonly document: ReaderDocument
}

/**
 * An assignment as the commissioning workspace sees it (api_requester)
 */
export type TenantAssignment = {
  readonly id: Uuid
  readonly createdAt: DateTimeString
  readonly status: AssignmentStatus
  readonly locale: string
  readonly isSelf: boolean
  readonly submittedAt: DateTimeString | null
  readonly revokedAt: DateTimeString | null
  readonly lastReminderAt: DateTimeString | null
  readonly canRemind: boolean
  readonly respondent: UserRef
  readonly createdBy: UserRef | null
  readonly brief: BriefRef | null
}

/**
 * The workspace detail response: the assignment (with its personal
 * message) plus the matrix + evaluation once Evaluated
 */
export type TenantAssignmentDetail = {
  readonly assignment: TenantAssignment & {
    readonly personalMessage: string | null
  }
  readonly matrix: ResultMatrix | null
  readonly evaluation: EvaluationView | null
}

/**
 * An assignment as its respondent sees it: the workspace is identified by
 * its public fields only — no brief, no requester (api_respondent)
 */
export type RespondentAssignment = {
  readonly id: Uuid
  readonly createdAt: DateTimeString
  readonly status: AssignmentStatus
  readonly locale: string
  readonly isSelf: boolean
  readonly submittedAt: DateTimeString | null
  readonly revokedAt: DateTimeString | null
  readonly tenant: TenantRef
}

/**
 * The evalee detail response: matrix + evaluation once Evaluated (and the
 * evalee-visibility policy allows it)
 */
export type RespondentAssignmentDetail = {
  readonly assignment: RespondentAssignment
  readonly matrix: ResultMatrix | null
  readonly evaluation: EvaluationView | null
}

export type AssignmentListFilter = {
  readonly status?: AssignmentStatus
  readonly respondentEmail?: string
  readonly briefId?: Uuid
  readonly oldestFirst?: boolean
  readonly page?: number
  readonly perPage?: number
}

export type SendAssignmentsRequest = {
  readonly emails: string[]
  readonly locale: string
  readonly personalMessage?: string | null
  readonly briefId?: Uuid | null
}

export type SelfAssignmentRequest = {
  readonly briefId?: Uuid | null
}
